import React, { useState, useRef, useEffect } from "react";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { api, loadUser } from "@/lib/api";

const SUGGESTED_PUBLIC = [
  "Find Python jobs for freshers",
  "What roles are hiring for AI/ML?",
  "Show upcoming hackathons",
  "What is the hiring process?",
];
const SUGGESTED_CANDIDATE = [
  "What is my application status?",
  "Which jobs match my profile?",
  "What assessments are pending?",
  "What should I do next?",
];

export default function VSAIWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I'm VS AI — your career assistant. Ask about jobs, hackathons, career events, or your applications." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sid, setSid] = useState(null);
  const bottomRef = useRef(null);
  const user = loadUser();
  const suggestions = user?.role === "candidate" ? SUGGESTED_CANDIDATE : SUGGESTED_PUBLIC;

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setMessages(m => [...m, { role: "user", text: msg }]);
    setInput(""); setLoading(true);
    try {
      const { data } = await api.post("/vs-ai/chat", { message: msg, session_id: sid });
      setSid(data.session_id);
      setMessages(m => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages(m => [...m, { role: "assistant", text: "Sorry, I couldn't reach VS AI just now. Please try again." }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(true)}
        data-testid="vs-ai-open"
        className={`fixed bottom-6 right-6 z-40 group ${open ? "hidden" : "flex"} items-center gap-2 rounded-full bg-[#0A2540] hover:bg-[#0A2540]/90 text-white pl-4 pr-5 py-3 shadow-[0_12px_30px_-8px_rgba(10,37,64,0.5)] transition-all`}
        aria-label="Open VS AI"
      >
        <div className="relative">
          <Sparkles className="w-4 h-4 text-[#F59E0B]"/>
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#F59E0B] animate-pulse"/>
        </div>
        <span className="text-sm font-semibold">VS AI</span>
      </button>

      {/* Chat panel */}
      {open && (
        <div className="fixed inset-x-3 bottom-3 md:inset-auto md:bottom-6 md:right-6 md:w-[400px] z-50 rounded-2xl bg-white border border-slate-200 shadow-2xl flex flex-col overflow-hidden" style={{maxHeight: "80vh"}} data-testid="vs-ai-panel">
          <div className="flex items-center justify-between px-4 py-3 bg-[#0A2540] text-white">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-[#F59E0B]/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-[#F59E0B]"/>
              </div>
              <div>
                <div className="text-sm font-semibold leading-tight">VS AI</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-300">Career Assistant</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded" data-testid="vs-ai-close"><X className="w-4 h-4"/></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-[#0A2540] text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl px-3 py-2 text-sm text-slate-500">
                  <span className="inline-flex gap-1"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce"/><span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay:"0.1s"}}/><span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay:"0.2s"}}/></span>
                </div>
              </div>
            )}
            <div ref={bottomRef}/>
          </div>
          {messages.length <= 1 && (
            <div className="px-3 py-2 border-t border-slate-200 bg-white">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1.5">Try asking</div>
              <div className="flex flex-wrap gap-1.5">
                {suggestions.map(s => (
                  <button key={s} onClick={() => send(s)} className="text-xs border border-slate-200 rounded-full px-2.5 py-1 hover:border-[#0A2540] hover:text-[#0A2540] transition-colors" data-testid="vs-ai-suggestion">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          <form onSubmit={e => { e.preventDefault(); send(); }} className="p-3 border-t border-slate-200 bg-white flex gap-2">
            <input value={input} onChange={e => setInput(e.target.value)}
              placeholder="Ask VS AI..." data-testid="vs-ai-input"
              className="flex-1 text-sm border border-slate-200 rounded-full px-4 py-2 focus:outline-none focus:border-[#0A2540]"/>
            <button type="submit" disabled={loading || !input.trim()} data-testid="vs-ai-send"
              className="p-2 rounded-full bg-[#0A2540] text-white hover:bg-[#0A2540]/90 disabled:opacity-50">
              <Send className="w-4 h-4"/>
            </button>
          </form>
        </div>
      )}
    </>
  );
}
